import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";

import {NOT_APPLICABLE_SYMBOL} from "./constants.ts";

import arrowDownIcon from "@phosphor-icons/core/regular/arrow-down.svg";
import arrowUpIcon from "@phosphor-icons/core/regular/arrow-up.svg";

type ChangeType = "negative" | "positive";

const changeIcons: Record<
    ChangeType,
    {
        icon: PhosphorRegular;
        color: string;
        label: string;
    }
> = {
    positive: {
        icon: arrowUpIcon,
        color: semanticColor.status.success.foreground,
        label: "positive change",
    },
    negative: {
        icon: arrowDownIcon,
        color: semanticColor.status.critical.foreground,
        label: "negative change",
    },
};

interface Props {
    change: number;
    noChangeLabel?: React.ReactNode;
    hideIfNoChange?: boolean;
}

const NotApplicable = ({noChangeLabel}: {noChangeLabel?: React.ReactNode}) => (
    <span className={css(styles.notApplicableContainer)}>{noChangeLabel}</span>
);

export const DefaultNotApplicable = (): React.ReactElement => (
    <NotApplicable noChangeLabel={NOT_APPLICABLE_SYMBOL} />
);

const ChangeIndicator = ({
    change,
    noChangeLabel,
    hideIfNoChange,
}: Props): React.ReactElement | null => {
    if (change === 0) {
        if (hideIfNoChange) {
            return null;
        }
        return <NotApplicable noChangeLabel={noChangeLabel} />;
    }

    const changeType = change > 0 ? "positive" : "negative";

    const changeIcon = changeIcons[changeType];

    return (
        <PhosphorIcon
            icon={changeIcon.icon}
            style={{
                color: changeIcon.color,
            }}
            size="medium"
            aria-hidden={true}
            aria-label={changeIcon.label}
        />
    );
};

ChangeIndicator.defaultProps = {
    noChangeLabel: NOT_APPLICABLE_SYMBOL,
};

const styles = StyleSheet.create({
    notApplicableContainer: {
        display: "inline-block",
        // hard-coded height and width to take up same space as icon
        width: 24,
        height: 24,
    },
});

export default ChangeIndicator;
